import { useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AuthContext } from "@/providers/auth-context";
import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";

/**
 * Holds the Supabase session for the app.
 *
 * The session itself lives in local storage and is refreshed by the Supabase
 * client; this only mirrors it into React so route guards and the header can
 * react to it. `onAuthStateChange` is the source of truth rather than a
 * one-off read, so a token refresh, an expiry, or a sign-out in another tab
 * all land here.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(
    // Nothing to wait for when there's no backend to ask.
    isSupabaseConfigured ? undefined : null,
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isSupabaseConfigured) return;

    const supabase = getSupabase();
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      // `onAuthStateChange` may have already fired with fresher state.
      if (active) setSession((current) => (current === undefined ? data.session : current));
    });

    const { data } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);

      // Cached rows belong to whoever was signed in. Dropping them on the way
      // out means the next person can't see them flash before a refetch.
      if (event === "SIGNED_OUT") queryClient.clear();
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [queryClient]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await getSupabase().auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await getSupabase().auth.signOut();
    if (error) throw new Error(error.message);
  }, []);

  const value = useMemo(() => ({ session, signIn, signOut }), [session, signIn, signOut]);

  return <AuthContext value={value}>{children}</AuthContext>;
}
